/**
 * Resistance Filter Visualization
 * Visualizes the equation E_f = E_i * (1 - R_f) from the Theory of Sovereign Reflectivity
 */



// Main p5.js sketch
const resistanceFilterSketch = (p) => {
    // Configuration
    const config = {
        colors: {
            primary: [44, 62, 80], // #2C3E50 (Deep indigo)
            secondary: [142, 68, 173], // #8E44AD (Cosmic purple)
            accent: [241, 196, 15], // #F1C40F (Gold)
            background: [18, 18, 18], // #121212 (Near-black)
            text: [245, 245, 245], // #F5F5F5 (Off-white)
            lowResistance: [46, 204, 113], // #2ECC71 (Green)
            highResistance: [231, 76, 60] // #E74C3C (Red)
        },
        showComparison: false
    };

    // Variables for the equation
    let initialEnergy = 1.0; // Initial energy (E_i)
    let resistanceFactor = 0.5; // Resistance factor (R_f)
    let finalEnergy = 0.5; // Final energy (E_f) (calculated)

    // UI elements
    let initialEnergySlider, resistanceFactorSlider;
    let initialEnergyValue, resistanceFactorValue, finalEnergyValue;
    let resetButton, toggleComparisonButton;

    // Animation variables
    let time = 0;
    let particles = [];
    let filterY;

    // Setup function
    p.setup = function () {
        // Create canvas
        const canvas = p.createCanvas(p.windowWidth > 800 ? 800 : p.windowWidth - 40, 500);
        const canvasContainer = document.getElementById('resistance-filter-canvas');
        if (canvasContainer) {
            canvas.parent('resistance-filter-canvas');
        } else {
            console.warn('Resistance filter canvas container not found, visualization may not display correctly');
        }

        // Initialize UI elements from DOM
        initialEnergySlider = document.getElementById('intention-slider');
        resistanceFactorSlider = document.getElementById('resistance-slider');
        initialEnergyValue = document.getElementById('intention-value');
        resistanceFactorValue = document.getElementById('resistance-value');
        finalEnergyValue = document.getElementById('outcome-value');
        resetButton = document.getElementById('reset-button');
        toggleComparisonButton = document.getElementById('toggle-comparison');

        // Add event listeners
        if (initialEnergySlider) initialEnergySlider.addEventListener('input', updateInitialEnergy);
        if (resistanceFactorSlider) resistanceFactorSlider.addEventListener('input', updateResistanceFactor);
        if (resetButton) resetButton.addEventListener('click', resetValues);
        if (toggleComparisonButton) toggleComparisonButton.addEventListener('click', toggleComparison);

        // Set filter position
        filterY = p.height / 2;

        // Initialize particles
        createParticles();

        // Calculate initial final energy
        calculateFinalEnergy();
    };

    // Draw function
    p.draw = function () {
        p.background(config.colors.background);

        if (config.showComparison) {
            drawComparisonView();
        } else {
            drawStandardView();
        }

        // Update animation time
        time += 0.02;
    };

    // Create particles for energy visualization
    function createParticles() {
        particles = [];
        const particleCount = 200;

        for (let i = 0; i < particleCount; i++) {
            particles.push({
                x: p.random(p.width),
                y: p.random(p.height),
                size: p.random(3, 8),
                speed: p.random(1, 3),
                angle: p.random(p.TWO_PI),
                color: p.lerpColor(
                    p.color(config.colors.lowResistance),
                    p.color(config.colors.highResistance),
                    p.random()
                ),
                blocked: false
            });
        }
    }

    // Update initial energy from slider
    function updateInitialEnergy() {
        if (!initialEnergySlider) return;

        initialEnergy = parseFloat(initialEnergySlider.value);
        if (initialEnergyValue) {
            initialEnergyValue.textContent = initialEnergy.toFixed(1);
        }
        calculateFinalEnergy();
    }

    // Update resistance factor from slider
    function updateResistanceFactor() {
        if (!resistanceFactorSlider) return;

        resistanceFactor = parseFloat(resistanceFactorSlider.value);
        if (resistanceFactorValue) {
            resistanceFactorValue.textContent = resistanceFactor.toFixed(2);
        }
        calculateFinalEnergy();
    }

    // Calculate final energy based on E_f = E_i * (1 - R_f)
    function calculateFinalEnergy() {
        finalEnergy = initialEnergy * (1 - resistanceFactor);
        if (finalEnergyValue) {
            finalEnergyValue.textContent = finalEnergy.toFixed(2);
        }
    }

    // Reset all values to defaults
    function resetValues() {
        if (!initialEnergySlider || !resistanceFactorSlider) return;

        initialEnergySlider.value = 1.0;
        resistanceFactorSlider.value = 0.5;
        updateInitialEnergy();
        updateResistanceFactor();
    }

    // Toggle comparison view
    function toggleComparison() {
        config.showComparison = !config.showComparison;
        if (toggleComparisonButton) {
            toggleComparisonButton.textContent = config.showComparison ? 'Hide Comparison' : 'Toggle Comparison';
        }
    }

    // Draw standard visualization view
    function drawStandardView() {
        // Draw background grid
        drawGrid();

        // Draw filter
        drawFilter();

        // Draw energy particles
        drawEnergyParticles();

        // Draw labels and equation
        drawLabels();
    }

    // Draw filter
    function drawFilter() {
        const filterHeight = 20;
        const filterWidth = p.width * 0.8;
        const filterX = p.width / 2 - filterWidth / 2;

        // Draw filter body
        p.noStroke();
        p.fill(config.colors.secondary[0], config.colors.secondary[1], config.colors.secondary[2], 150);
        p.rect(filterX, filterY - filterHeight / 2, filterWidth, filterHeight);

        // Draw filter border
        p.stroke(config.colors.secondary);
        p.strokeWeight(2);
        p.noFill();
        p.rect(filterX, filterY - filterHeight / 2, filterWidth, filterHeight);

        // Draw resistance indicator
        const indicatorWidth = filterWidth * resistanceFactor;
        p.noStroke();
        p.fill(config.colors.highResistance[0], config.colors.highResistance[1], config.colors.highResistance[2], 150);
        p.rect(filterX, filterY - filterHeight / 2, indicatorWidth, filterHeight);

        // Draw filter label
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(14);
        p.text("Resistance Filter", p.width / 2, filterY - filterHeight / 2 - 20);
        p.textSize(12);
        p.text(`R_f = ${resistanceFactor.toFixed(2)}`, p.width / 2, filterY + filterHeight / 2 + 20);
    }

    // Draw energy particles
    function drawEnergyParticles() {
        // Check if particles array is initialized
        if (!particles || particles.length === 0) {
            console.warn('Particles array not initialized, recreating particles');
            createParticles();
            return;
        }

        const filterHeight = 20;
        const filterWidth = p.width * 0.8;
        const filterX = p.width / 2 - filterWidth / 2;
        const particleCount = Math.floor(200 * initialEnergy);

        for (let i = 0; i < particleCount; i++) {
            // Make sure we don't exceed the particles array length
            if (i >= particles.length) break;

            const particle = particles[i];

            // Skip if particle is undefined
            if (!particle) continue;

            // Update position
            if (!particle.blocked) {
                particle.x += Math.cos(particle.angle) * particle.speed;
                particle.y += Math.sin(particle.angle) * particle.speed;
            } else {
                // Blocked particles move more slowly and randomly
                particle.x += (Math.random() - 0.5) * 0.5;
                particle.y += (Math.random() - 0.5) * 0.5;
            }

            // Wrap around edges
            if (particle.x < 0) particle.x = p.width;
            if (particle.x > p.width) particle.x = 0;
            if (particle.y < 0) particle.y = p.height;
            if (particle.y > p.height) particle.y = 0;

            // Check if particle is passing through filter
            if (Math.abs(particle.y - filterY) < filterHeight / 2 &&
                particle.x > filterX && particle.x < filterX + filterWidth) {

                // Determine if particle is blocked based on resistance factor
                if (Math.random() < resistanceFactor) {
                    // Particle is blocked
                    particle.blocked = true;
                    particle.color = p.color(config.colors.highResistance);
                    particle.angle = p.random(p.TWO_PI); // Random new direction
                } else {
                    // Particle passes through
                    particle.blocked = false;
                    particle.color = p.color(config.colors.lowResistance);
                }
            }

            // Draw particle
            p.noStroke();
            p.fill(particle.color);
            p.ellipse(particle.x, particle.y, particle.size);
        }
    }

    // Draw background grid
    function drawGrid() {
        p.stroke(config.colors.primary[0], config.colors.primary[1], config.colors.primary[2], 50);
        p.strokeWeight(1);

        const gridSize = 30;
        for (let x = 0; x < p.width; x += gridSize) {
            p.line(x, 0, x, p.height);
        }

        for (let y = 0; y < p.height; y += gridSize) {
            p.line(0, y, p.width, y);
        }
    }

    // Draw comparison view (low vs high resistance)
    function drawComparisonView() {
        // Draw dividing line
        p.stroke(config.colors.accent);
        p.strokeWeight(2);
        p.line(p.width / 2, 0, p.width / 2, p.height);

        // Draw titles
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(16);
        p.text("Low Resistance (R_f = 0.2)", p.width * 0.25, 30);
        p.text("High Resistance (R_f = 0.8)", p.width * 0.75, 30);

        // Draw equations
        p.fill(config.colors.accent);
        p.textSize(20);
        p.text(`E_f = 1.0 × (1 - 0.2) = 0.8`, p.width * 0.25, 60);
        p.text(`E_f = 1.0 × (1 - 0.8) = 0.2`, p.width * 0.75, 60);

        // Draw low resistance side (left)
        drawResistanceScenario(p.width * 0.25, 0.2);

        // Draw high resistance side (right)
        drawResistanceScenario(p.width * 0.75, 0.8);

        // Draw explanation
        p.fill(config.colors.text);
        p.textSize(14);
        p.textAlign(p.CENTER, p.CENTER);
        p.text("Most energy passes through", p.width * 0.25, p.height - 40);
        p.text("Most energy is blocked", p.width * 0.75, p.height - 40);
    }

    // Draw a resistance scenario for the comparison view
    function drawResistanceScenario(centerX, resistanceValue) {
        const centerY = p.height / 2;
        const boxSize = 120;
        const filterY = centerY;
        const filterHeight = 20;

        // Draw container box
        p.noFill();
        p.stroke(config.colors.text);
        p.strokeWeight(1);
        p.rect(centerX - boxSize / 2, centerY - boxSize / 2, boxSize, boxSize);

        // Draw filter
        p.noStroke();
        p.fill(p.lerpColor(
            p.color(config.colors.lowResistance),
            p.color(config.colors.highResistance),
            resistanceValue
        ));
        p.rect(centerX - boxSize / 2, filterY - filterHeight / 2, boxSize, filterHeight);

        // Draw filter border
        p.stroke(config.colors.secondary);
        p.strokeWeight(2);
        p.noFill();
        p.rect(centerX - boxSize / 2, filterY - filterHeight / 2, boxSize, filterHeight);

        // Draw particles
        const particleCount = 50;
        const passedCount = Math.floor(particleCount * (1 - resistanceValue));
        const blockedCount = particleCount - passedCount;

        // Draw particles above filter (input)
        for (let i = 0; i < particleCount; i++) {
            const angle = p.random(p.TWO_PI);
            const distance = p.random(boxSize * 0.4);
            const x = centerX + Math.cos(angle) * distance;
            const y = centerY - boxSize * 0.25 + Math.sin(angle) * distance * 0.5;

            p.noStroke();
            p.fill(config.colors.accent);
            p.ellipse(x, y, 5);
        }

        // Draw particles below filter (output)
        for (let i = 0; i < passedCount; i++) {
            const angle = p.random(p.TWO_PI);
            const distance = p.random(boxSize * 0.4);
            const x = centerX + Math.cos(angle) * distance;
            const y = centerY + boxSize * 0.25 + Math.sin(angle) * distance * 0.5;

            p.noStroke();
            p.fill(config.colors.lowResistance);
            p.ellipse(x, y, 5);
        }

        // Draw blocked particles at filter
        for (let i = 0; i < blockedCount; i++) {
            const x = centerX - boxSize / 2 + p.random(boxSize);
            const y = filterY + p.random(-filterHeight / 2, filterHeight / 2);

            p.noStroke();
            p.fill(config.colors.highResistance);
            p.ellipse(x, y, 5);
        }

        // Draw energy values
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(14);
        p.text(`Input: 1.0`, centerX, centerY - boxSize / 2 - 20);
        p.text(`Output: ${(1 - resistanceValue).toFixed(1)}`, centerX, centerY + boxSize / 2 + 20);

        // Draw resistance label
        p.textSize(12);
        p.text(`Resistance: ${resistanceValue.toFixed(1)}`, centerX, filterY);
    }

    // Draw labels and equation
    function drawLabels() {
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);

        // Draw equation at the top
        p.textSize(24);
        p.fill(config.colors.accent);
        p.text(`E_f = E_i × (1 - R_f)`, p.width / 2, 30);

        // Draw current values
        p.textSize(16);
        p.fill(config.colors.text);
        p.text(`E_i = ${initialEnergy.toFixed(1)}   R_f = ${resistanceFactor.toFixed(2)}   E_f = ${finalEnergy.toFixed(2)}`, p.width / 2, 60);

        // Draw input and output labels
        p.textSize(14);
        p.text("Input Energy", p.width / 2, filterY - 60);
        p.text("Output Energy", p.width / 2, filterY + 60);

        // Draw arrows
        p.stroke(config.colors.text);
        p.strokeWeight(2);
        p.fill(config.colors.text);
        drawArrow(p.width / 2, filterY - 40, p.width / 2, filterY - 10);
        drawArrow(p.width / 2, filterY + 10, p.width / 2, filterY + 40);
    }

    // Helper function to draw arrows
    function drawArrow(x1, y1, x2, y2) {
        p.line(x1, y1, x2, y2);

        const angle = p.atan2(y2 - y1, x2 - x1);
        const arrowSize = 10;

        p.push();
        p.translate(x2, y2);
        p.rotate(angle);
        p.triangle(-arrowSize, arrowSize / 2, -arrowSize, -arrowSize / 2, 0, 0);
        p.pop();
    }

    // Handle window resize
    p.windowResized = function () {
        p.resizeCanvas(p.windowWidth > 800 ? 800 : p.windowWidth - 40, 500);
        filterY = p.height / 2;
    };
};

// Initialize the p5.js sketch
new p5(resistanceFilterSketch);
